import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import { DollarSign, FileText, Megaphone } from "lucide-react";
import type { NotificationItem } from "@/api/notifications";

const slideIn = keyframes`
	from {
		opacity: 0;
		transform: translateY(12px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

const Overlay = styled.div`
	position: fixed;
	right: 24px;
	bottom: 24px;
	z-index: 3100;
	width: min(360px, calc(100vw - 32px));
	pointer-events: none;
`;

const Card = styled.div`
	pointer-events: auto;
	background: #ffffff;
	border-radius: 16px;
	box-shadow: 0 12px 40px rgba(21, 89, 165, 0.18);
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	animation: ${slideIn} 220ms ease-out;
`;

const TopRow = styled.div`
	display: grid;
	grid-template-columns: 36px 1fr auto;
	gap: 10px;
	align-items: start;
`;

const IconBadge = styled.div`
	width: 36px;
	height: 36px;
	border-radius: 10px;
	background: #eeedfe;
	color: #3c3489;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
`;

const MetaCol = styled.div`
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 4px;
`;

const AppName = styled.span`
	font-family: Poppins, sans-serif;
	font-size: 12px;
	font-weight: 500;
	color: #9b9b9b;
`;

const TimeLabel = styled.span`
	font-family: Poppins, sans-serif;
	font-size: 12px;
	font-weight: 500;
	color: #9b9b9b;
	white-space: nowrap;
	padding-top: 2px;
`;

const Title = styled.div`
	font-family: Poppins, sans-serif;
	font-size: 15px;
	font-weight: 600;
	color: #141414;
	line-height: 1.3;
`;

const Body = styled.div`
	font-family: Poppins, sans-serif;
	font-size: 13px;
	font-weight: 400;
	color: #606060;
	line-height: 1.4;
`;

const Actions = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 8px;
`;

const PrimaryButton = styled.button`
	border: none;
	border-radius: 10px;
	background: #eeedfe;
	color: #3c3489;
	font-family: Poppins, sans-serif;
	font-size: 13px;
	font-weight: 500;
	padding: 10px 12px;
	cursor: pointer;

	&:hover {
		background: #e4e2fb;
	}
`;

const SecondaryButton = styled.button`
	border: 1px solid #e5e5e5;
	border-radius: 10px;
	background: #ffffff;
	color: #606060;
	font-family: Poppins, sans-serif;
	font-size: 13px;
	font-weight: 500;
	padding: 10px 12px;
	cursor: pointer;

	&:hover {
		background: #fafafa;
	}
`;

function toastTitle(item: NotificationItem): string {
	if (item.type === "CS1") return "Creator accepted invite";
	if (item.type === "CS2") return "Creator declined invite";
	if (item.type === "C1") return "New campaign added";
	if (item.type === "C2") return "Engagement terms ready";
	return item.title;
}

function toastBody(item: NotificationItem): string {
	if (item.type === "CS1") {
		if (item.body?.includes("can now be assigned")) return item.body;
		const firstName = item.actorName?.trim().split(/\s+/)[0];
		const campaign = item.campaignName?.trim();
		if (firstName && campaign) {
			return `${firstName} has confirmed participation for ${campaign}. They can now be assigned deliverables.`;
		}
	}
	if (item.type === "CS2") {
		if (item.body?.includes("Consider sourcing a backup creator")) return item.body;
		const firstName = item.actorName?.trim().split(/\s+/)[0];
		if (firstName) {
			return `${firstName} is unavailable. Consider sourcing a backup creator.`;
		}
	}
	if (item.type === "C1" && item.campaignName) {
		return `You've been added to ${item.campaignName}. Review your deliverables and timeline.`;
	}
	if (item.type === "C2") {
		return "Your creator fee and engagement terms have been set. Review and accept to move into production.";
	}
	return item.body;
}

function toastCta(item: NotificationItem): string {
	if (item.type === "CS1") return "View Creator";
	if (item.type === "CS2") return "Find Replacement";
	if (item.type === "C1") return "View campaign";
	if (item.type === "C2") return "Review Terms";
	return item.ctaLabel || "Open";
}

function toastIcon(item: NotificationItem) {
	if (item.type === "C2") return <FileText size={18} />;
	if (item.type === "C4") return <DollarSign size={18} />;
	return <Megaphone size={18} />;
}

interface NotificationToastProps {
	item: NotificationItem;
	onView: (item: NotificationItem) => void;
	onDismiss: () => void;
}

export function NotificationToast({ item, onView, onDismiss }: NotificationToastProps) {
	if (typeof document === "undefined") return null;

	return createPortal(
		<Overlay role="status" aria-live="polite">
			<Card>
				<TopRow>
					<IconBadge>{toastIcon(item)}</IconBadge>
					<MetaCol>
						<AppName>Creatorverse</AppName>
						<Title>{toastTitle(item)}</Title>
						<Body>{toastBody(item)}</Body>
					</MetaCol>
					<TimeLabel>now</TimeLabel>
				</TopRow>
				<Actions>
					<PrimaryButton type="button" onClick={() => onView(item)}>
						{toastCta(item)}
					</PrimaryButton>
					<SecondaryButton type="button" onClick={onDismiss}>
						Dismiss
					</SecondaryButton>
				</Actions>
			</Card>
		</Overlay>,
		document.body,
	);
}
